import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';

import { KolQuestionnaireService } from 'app/_services';

/**
 * Public, unauthenticated KOL questionnaire page. Reached via the tokenized
 * link emailed to a contact (see backend contactSubmissionCtrl.sendQuestionnaireEmail).
 * The token identifies the company + contact; the matched SpeakerTieringForm is
 * rendered screen-by-screen and answers post back to the same store the admin
 * Questionnaire tab reads.
 */
@Component({
  selector: 'app-kol-questionnaire',
  templateUrl: './kol-questionnaire.component.html',
  styleUrls: ['./kol-questionnaire.component.scss'],
})
export class KolQuestionnaireComponent implements OnInit {
  token = '';
  loading = true;
  submitting = false;
  submitted = false;
  error: string | null = null;

  company: any = null;
  contact: any = null;
  form: any = null;
  esifFormId: any = null;
  data: any = {};

  /** null | 'draft' | 'submitted' — questionnaire status, distinct from contract status. */
  questionnaireStatus: string | null = null;

  /** Index of the screen currently shown (the form is paginated screen-by-screen). */
  currentScreenIndex = 0;
  saving = false;

  private questionsById: { [id: number]: any } = {};

  constructor(
    private route: ActivatedRoute,
    private kolQuestionnaireService: KolQuestionnaireService,
    private toastr: ToastrService,
  ) { }

  ngOnInit() {
    this.token = this.route.snapshot.queryParamMap.get('token') || '';
    if (!this.token) {
      this.loading = false;
      this.error = 'This questionnaire link is missing its token.';
      return;
    }
    this.kolQuestionnaireService.get(this.token).subscribe(
      (res) => {
        this.loading = false;
        this.company = res.company;
        this.contact = res.contact;
        this.form = res.form;
        this.esifFormId = res.esif_form_id;
        this.data = res.data || {};
        this.questionnaireStatus = res.questionnaire_status || null;
        (res.questions || []).forEach((q) => { this.questionsById[q.id] = q; });
      },
      () => {
        this.loading = false;
        this.error = 'This questionnaire link is invalid or has expired.';
      },
    );
  }

  /** Look up a Question by id when rendering a screen (mirrors the admin tab). */
  getQuestion(id: number) {
    return this.questionsById[id];
  }

  get screens(): any[] {
    return (this.form && this.form.screens) || [];
  }

  get isLastScreen(): boolean {
    return this.currentScreenIndex >= this.screens.length - 1;
  }

  prevScreen() {
    if (this.currentScreenIndex > 0) {
      this.currentScreenIndex -= 1;
      this.scrollToTop();
    }
  }

  nextScreen() {
    if (this.currentScreenIndex < this.screens.length - 1) {
      this.saveProgress();
      this.currentScreenIndex += 1;
      this.scrollToTop();
    }
  }

  /**
   * Persist the current answers (clicking Next saves). This is NOT a submit —
   * the backend leaves a submitted form alone and only stamps 'draft' when the
   * questionnaire has no status yet. Fired in the background so paging stays snappy.
   */
  private saveProgress() {
    if (!this.esifFormId) {
      return;
    }
    this.saving = true;
    this.kolQuestionnaireService.save(this.token, this.esifFormId, this.data).subscribe(
      (res) => {
        this.saving = false;
        if (res && res.questionnaire_status) {
          this.questionnaireStatus = res.questionnaire_status;
        }
      },
      () => {
        this.saving = false;
        this.toastr.error('Could not save your progress. Please check your connection.');
      },
    );
  }

  private scrollToTop() {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  onSubmit() {
    if (this.submitting) return;
    // Guard against an accidental Enter-key submit on an earlier screen: only
    // the final screen submits; otherwise advance to the next screen.
    if (!this.isLastScreen) {
      this.nextScreen();
      return;
    }
    this.submitting = true;
    this.kolQuestionnaireService.submit(this.token, this.esifFormId, this.data).subscribe(
      () => {
        this.submitting = false;
        this.submitted = true;
        this.questionnaireStatus = 'submitted';
        this.toastr.success('Thank you! Your questionnaire has been submitted.');
      },
      () => {
        this.submitting = false;
        this.toastr.error('Something went wrong submitting your questionnaire. Please try again.');
      },
    );
  }
}
